import L from 'leaflet';
import {
  getColorMultiplierFromSensitivity,
  getTrendColorFromDelta,
  getTrendColorFromScale,
  resolveTrendColorSchemeId
} from './trendColorScheme.js';

/**
 * Manages aircraft markers and their trails on the map
 */
export class AircraftManager {
  constructor(map) {
    this.map = map;
    this.aircraft = new Map(); // cookie -> { marker, trail, positions, color }
    this.maxTrailPoints = 100;
    this.usedColors = new Set();
    this.infoLabel = 'id'; // 'id', 'speed', 'altitude', 'vario', 'vario_avg', 'vario_kalman', 'vario_te', 'etf_delta', 'etf_delta_b', 'ddh'
    this.units = 'imperial'; // 'imperial' or 'metric'
    this.colorScheme = 'aircraft'; // 'aircraft', 'altitude', 'speed', 'vario', 'vario_avg', 'vario_kalman', 'vario_te', 'etf_delta', 'etf_delta_b'
    this.colorSensitivity = 0; // -100 to +100, 0 = default (75% gradient)
    this.trendColorScheme = 'gain_loss'; // Shared ETF/XC trend palette id
    this.followingCookie = null; // Cookie of aircraft being followed
    this.followCallback = null; // Callback to update UI

    // EMA smoothing factor for 30-second average (assuming ~1Hz updates)
    // α = 2 / (N + 1), where N ≈ 30 for 30-second window
    this.varioEmaAlpha = 2 / (30 + 1); // ≈ 0.0645

    // Kalman filter parameters for vario smoothing
    // Process noise (how much we expect vario to change naturally)
    this.kalmanQ = 0.01; // Lower = smoother but slower response
    // Measurement noise (how noisy is the vario sensor)
    this.kalmanR = 0.5; // Higher = more smoothing

    // Total Energy compensation constants
    // For a high-performance glider, typical best glide speed ~50-60 kt (~25-30 m/s)
    this.GRAVITY = 9.81; // m/s^2
    // TE tuning parameters
    this.teGain = 1.0; // Multiplier on TE compensation term
    this.teAccelLimit = 3.0; // Clamp dv/dt (m/s^2) to reduce noise/overcompensation

    // Hidden aircraft tracking for visibility toggle
    this.hiddenCookies = new Set();

    // Label refresh throttle (ms). 0 = no throttle (update every call).
    // Controlled by Replay Refresh Rate slider.
    this.labelRefreshMs = 0;

    // Replay trail-head smoothing. This keeps the visible trail head sampled
    // continuously so replay does not appear to lay down one coarse chunk per second.
    this.replayTrailHeadWindowMs = 2500;
    this.replayTrailHeadSampleMs = 150;
    this.replayTrailHeadMinDistanceM = 12;
  }

  /**
   * Set color sensitivity (-100 to +100)
   * 0 = no change (default gradient)
   * -100 = minimum sensitivity (colors change more slowly)
   * +100 = maximum sensitivity (colors reach extremes faster)
   */
  setColorSensitivity(sensitivity) {
    this.colorSensitivity = Math.max(-100, Math.min(100, sensitivity || 0));
  }

  /**
   * Set ETF/XC trend color scheme id.
   */
  setTrendColorScheme(schemeId) {
    this.trendColorScheme = resolveTrendColorSchemeId(schemeId);
  }

  normalizeHeadingDegrees(heading) {
    if (!Number.isFinite(heading)) return 0;
    const normalized = heading % 360;
    return normalized < 0 ? normalized + 360 : normalized;
  }

  getIconHeadingDegrees(heading) {
    const normalized = this.normalizeHeadingDegrees(heading);
    return this.normalizeHeadingDegrees(Math.round(normalized / 5) * 5);
  }

  getAircraftIconKey(heading, color, label) {
    const renderedHeading = this.getIconHeadingDegrees(heading);
    return `${renderedHeading}|${color || ''}|${label || ''}`;
  }

  applyAircraftIcon(aircraftData, heading, color, label) {
    if (!aircraftData?.marker) return false;

    const renderedHeading = this.getIconHeadingDegrees(heading);
    const iconKey = this.getAircraftIconKey(renderedHeading, color, label);
    if (aircraftData.lastIconKey === iconKey) {
      return false;
    }

    const icon = this.createAircraftIcon(renderedHeading, color, label);
    aircraftData.marker.setIcon(icon);
    aircraftData.lastIconKey = iconKey;
    aircraftData.lastIconHeading = renderedHeading;
    return true;
  }

  getPositionTimestampMs(position) {
    const timestamp = position?.timestamp;
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }
    if (timestamp) {
      const parsedMs = new Date(timestamp).getTime();
      if (Number.isFinite(parsedMs)) {
        return parsedMs;
      }
    }
    return Date.now();
  }

  clearPendingTrailHistory(aircraftData) {
    if (!aircraftData) return;
    aircraftData.pendingTrailHistory = [];
    aircraftData.pendingTrailLastTimeMs = null;
  }

  updatePendingTrailHistory(aircraftData, position) {
    if (!aircraftData || !position) {
      return [];
    }

    const { lat, lon } = position;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      return aircraftData.pendingTrailHistory || [];
    }

    const timestampMs = this.getPositionTimestampMs(position);
    let history = aircraftData.pendingTrailHistory || [];
    const lastPoint = history[history.length - 1];

    if (lastPoint && timestampMs < lastPoint.timestampMs - 250) {
      history = [];
    }

    if (history.length === 0) {
      history = [{ lat, lon, timestampMs }];
    } else {
      const lastHistoryPoint = history[history.length - 1];
      const elapsedMs = timestampMs - lastHistoryPoint.timestampMs;
      const distanceMeters = this.calculateDistance(lastHistoryPoint.lat, lastHistoryPoint.lon, lat, lon) * 1000;
      const shouldAppend =
        elapsedMs >= this.replayTrailHeadSampleMs ||
        distanceMeters >= this.replayTrailHeadMinDistanceM;

      if (shouldAppend) {
        history.push({ lat, lon, timestampMs });
      } else {
        lastHistoryPoint.lat = lat;
        lastHistoryPoint.lon = lon;
        lastHistoryPoint.timestampMs = timestampMs;
      }
    }

    while (history.length > 1 && (timestampMs - history[0].timestampMs) > this.replayTrailHeadWindowMs) {
      history.shift();
    }

    aircraftData.pendingTrailHistory = history;
    aircraftData.pendingTrailLastTimeMs = timestampMs;
    return history;
  }

  /**
   * Get color gradient multiplier based on sensitivity setting
   * Returns a value between 0.5 (at -100) and 2.0 (at +100)
   * Default (0) returns 1.0 (no change)
   *
   * Slider: Left = less sensitive (dampened), Right = more sensitive (amplified)
   */
  getColorMultiplier() {
    return getColorMultiplierFromSensitivity(this.colorSensitivity);
  }

  /**
   * Generates a unique vibrant color for each aircraft with rapid color changes
   */
  generateUniqueColor() {
    // Use golden ratio for maximum color distribution
    const goldenRatio = 0.618033988749895;
    const hue = (this.usedColors.size * goldenRatio * 360) % 360;
    // Vary saturation and lightness for more variety
    const saturation = 70 + (this.usedColors.size % 3) * 10; // 70%, 80%, 90%
    const lightness = 50 + (this.usedColors.size % 2) * 10; // 50%, 60%
    const color = `hsl(${Math.floor(hue)}, ${saturation}%, ${lightness}%)`;
    this.usedColors.add(color);
    return color;
  }

  /**
   * Gets color based on altitude (in feet) using the active universal scheme.
   */
  getAltitudeColor(altFt) {
    const center = 5000;
    const multiplier = this.getColorMultiplier();
    const scaledAlt = center + (altFt - center) * multiplier;
    const lowSpan = center - 1000;
    const highSpan = 11000 - center;
    const scaleValue = scaledAlt <= center
      ? ((scaledAlt - center) / Math.max(1, lowSpan)) * 14
      : ((scaledAlt - center) / Math.max(1, highSpan)) * 14;

    return getTrendColorFromScale(scaleValue, this.trendColorScheme) || 'rgb(136, 136, 136)';
  }

  /**
   * Gets color based on speed (in knots) using the active universal scheme.
   */
  getSpeedColor(speedKt) {
    const center = 100;
    const multiplier = this.getColorMultiplier();
    const scaledSpeed = center + (speedKt - center) * multiplier;
    const lowSpan = center - 70;
    const highSpan = 160 - center;
    const scaleValue = scaledSpeed <= center
      ? ((scaledSpeed - center) / Math.max(1, lowSpan)) * 14
      : ((scaledSpeed - center) / Math.max(1, highSpan)) * 14;

    return getTrendColorFromScale(scaleValue, this.trendColorScheme) || 'rgb(136, 136, 136)';
  }

  /**
   * 1D Kalman filter update step
   * @param {Object} state - Kalman state {x: estimate, p: error covariance}
   * @param {number} measurement - New measurement
   * @param {number} dt - Time delta in seconds
   * @returns {Object} Updated state {x, p}
   */
  kalmanUpdate(state, measurement, dt) {
    // Predict step
    let x = state.x; // State estimate (no prediction model, assume constant)
    let p = state.p + this.kalmanQ * dt; // Error covariance grows with time

    // Update step
    const k = p / (p + this.kalmanR); // Kalman gain
    x = x + k * (measurement - x); // Update estimate
    p = (1 - k) * p; // Update error covariance

    return { x, p };
  }

  /**
   * Calculates Total Energy (TE) compensated vario
   * TE vario = altitude_rate + (speed × speed_rate) / g
   * This compensates for kinetic energy changes (speed changes) to show only potential energy changes
   * @param {number} vario_mps - Raw vario in m/s
   * @param {number} speed_mps - Current speed in m/s
   * @param {number} prevSpeed_mps - Previous speed in m/s
   * @param {number} dt - Time delta in seconds
   * @returns {number} TE compensated vario in m/s
   */
  calculateTEVario(vario_mps, speed_mps, prevSpeed_mps, dt) {
    if (!speed_mps || !prevSpeed_mps || dt <= 0 || dt > 10) {
      // If we don't have valid speed data or time delta, return raw vario
      return vario_mps;
    }

    // Calculate speed rate (acceleration in m/s^2)
    const speedRateRaw = (speed_mps - prevSpeed_mps) / dt;
    const speedRate = Math.max(-this.teAccelLimit, Math.min(this.teAccelLimit, speedRateRaw));

    // TE compensation: add kinetic energy change converted to potential energy
    // TE_vario = vario + (v × dv/dt) / g
    const teCompensation = (speed_mps * speedRate) / this.GRAVITY;
    const teVario = vario_mps + (this.teGain * teCompensation);

    return teVario;
  }

  /**
   * Set TE tuning parameters
   * @param {number} gain - Multiplier for TE compensation term (default 1.0)
   * @param {number} accelLimit - Clamp for dv/dt in m/s^2 (default 3.0)
   */
  setTEParams(gain = null, accelLimit = null) {
    if (typeof gain === 'number') this.teGain = gain;
    if (typeof accelLimit === 'number') this.teAccelLimit = accelLimit;
  }

  /**
   * Gets color based on vario (in knots) using the active universal scheme.
   * @param {number} varioKt - Vario in knots
   * @param {boolean} useAverage - If true, use 30s averaged vario color scale
   */
  getVarioColor(varioKt, useAverage = false) {
    const multiplier = this.getColorMultiplier();
    const scaledVario = varioKt * multiplier;
    return getTrendColorFromScale(scaledVario, this.trendColorScheme) || 'rgb(136, 136, 136)';
  }

  /**
   * Gets color for ETF delta trend using the active trend-color scheme.
   * Scheme + sensitivity are centralized in trendColorScheme.js.
   */
  getETFDeltaColor(deltaSeconds, trendSeconds = 120) {
    return getTrendColorFromDelta(deltaSeconds, trendSeconds, {
      multiplier: this.getColorMultiplier(),
      schemeId: this.trendColorScheme,
      // Global rule is low=red/high=green; XC deltas are inverted because negative is better.
      invertSign: true,
      nullColor: 'rgb(136, 136, 136)'
    });
  }

  /**
   * Interpolates between two RGB colors
   */
  interpolateColor(color1, color2, t) {
    const rgb1 = color1.match(/\d+/g).map(Number);
    const rgb2 = color2.match(/\d+/g).map(Number);
    const r = Math.round(rgb1[0] + (rgb2[0] - rgb1[0]) * t);
    const g = Math.round(rgb1[1] + (rgb2[1] - rgb1[1]) * t);
    const b = Math.round(rgb1[2] + (rgb2[2] - rgb1[2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Gets the color for an aircraft based on current color scheme
   */
  getColorForAircraft(position, baseColor, cookie = null) {
    // Color-direction rule for future maintainers:
    // - Altitude/Speed/Vario: lower values map red, higher values map green.
    // - XC Pace (ETA delta A/B): sign is inverted because negative means better/faster.
    if (this.colorScheme === 'aircraft') {
      return baseColor;
    } else if (this.colorScheme === 'altitude') {
      // Parse altitude (API returns strings!)
      const altM = parseFloat(position.alt_m || position.altitude_m);
      const altFt = !isNaN(altM) ? altM * 3.28084 : 0; // Always use feet for color calculation
      return this.getAltitudeColor(altFt);
    } else if (this.colorScheme === 'speed') {
      // Parse speed (API returns strings!)
      const speedMps = parseFloat(position.speed_mps);
      const speedKt = !isNaN(speedMps) ? speedMps * 1.94384 : 0;
      return this.getSpeedColor(speedKt);
    } else if (this.colorScheme === 'vario') {
      // Parse vario (API returns strings!)
      const varioMps = parseFloat(position.vario_mps);
      const varioKt = !isNaN(varioMps) ? varioMps * 1.94384 : 0;
      return this.getVarioColor(varioKt, false);
    } else if (this.colorScheme === 'vario_avg') {
      // Use 30-second averaged vario for color
      // For trail segments: use stored value in position data
      // For current marker: look up current aircraft value
      let varioAvgMps;
      if (position.vario_avg_mps !== undefined) {
        // Trail segment - use stored value
        varioAvgMps = position.vario_avg_mps;
      } else {
        // Current marker - look up aircraft's current averaged value
        const aircraftData = cookie ? this.aircraft.get(cookie) : null;
        varioAvgMps = aircraftData?.varioAvg ?? position.vario_mps;
      }
      const varioKt = varioAvgMps * 1.94384;
      return this.getVarioColor(varioKt, true);
    } else if (this.colorScheme === 'vario_kalman') {
      // Use Kalman filtered vario for color
      // For trail segments: use stored value in position data
      // For current marker: look up current aircraft value
      let varioKalmanMps;
      if (position.vario_kalman_mps !== undefined) {
        // Trail segment - use stored value
        varioKalmanMps = position.vario_kalman_mps;
      } else {
        // Current marker - look up aircraft's current Kalman value
        const aircraftData = cookie ? this.aircraft.get(cookie) : null;
        varioKalmanMps = aircraftData?.varioKalman?.x ?? position.vario_mps;
      }
      const varioKt = varioKalmanMps * 1.94384;
      return this.getVarioColor(varioKt, true);
    } else if (this.colorScheme === 'vario_te') {
      // Use Total Energy compensated vario for color
      let varioTEMps;
      if (position.vario_te_mps !== undefined) {
        // Trail segment - use stored value
        varioTEMps = position.vario_te_mps;
      } else {
        // Current marker - look up aircraft's current TE value
        const aircraftData = cookie ? this.aircraft.get(cookie) : null;
        varioTEMps = aircraftData?.varioTE ?? position.vario_mps;
      }
      const varioKt = varioTEMps * 1.94384;
      return this.getVarioColor(varioKt, true);
    } else if (this.colorScheme === 'etf_delta') {
      const deltaSeconds = position.etfDeltaSeconds;
      const trendSeconds = position.etfTrendSec;
      if (!Number.isFinite(deltaSeconds)) {
        return null;
      }
      return this.getETFDeltaColor(deltaSeconds, trendSeconds);
    } else if (this.colorScheme === 'etf_delta_b') {
      const deltaSeconds = position.etfDeltaSecondsB;
      const trendSeconds = position.etfTrendSecB || position.etfTrendSec;
      if (!Number.isFinite(deltaSeconds)) {
        return null;
      }
      return this.getETFDeltaColor(deltaSeconds, trendSeconds);
    }
    return baseColor;
  }

  /**
   * Sets the color scheme and redraws trails with new colors
   */
  setColorScheme(scheme) {
    this.colorScheme = scheme;

    // Update all aircraft colors and redraw trails without clearing positions
    this.aircraft.forEach((data, cookie) => {
      // Update marker color
      if (data.positionData) {
        const newColor = this.getColorForAircraft(data.positionData, data.baseColor, cookie);
        const label = this.getLabel(data.positionData);
        const iconColor = newColor || data.baseColor;
        const newIcon = this.createAircraftIcon(data.lastHeading, iconColor, label);
        data.marker.setIcon(newIcon);
      }

      // Redraw trails based on new color scheme
      const isHidden = this.hiddenCookies.has(cookie);

      if (data.positions.length > 1) {
        if (scheme === 'aircraft') {
          // Switch to single-color trail
          // Remove any existing trail segments
          if (data.trailSegments) {
            data.trailSegments.forEach(seg => this.map.removeLayer(seg));
            data.trailSegments = [];
          }

          // Create or update single trail
          if (!data.trail) {
            data.trail = L.polyline(data.positions, {
              color: data.baseColor,
              weight: 3,
              opacity: 0.7,
              smoothFactor: 1,
              className: 'aircraft-trail'
            });
            // Only add to map if not hidden
            if (!isHidden) {
              data.trail.addTo(this.map);
            }
          } else {
            data.trail.setStyle({ color: data.baseColor });
            data.trail.setLatLngs(data.positions);
          }
        } else {
          // Switch to colored segments
          // Remove single trail if exists
          if (data.trail) {
            this.map.removeLayer(data.trail);
            data.trail = null;
          }

          // Clear existing segments
          if (data.trailSegments) {
            data.trailSegments.forEach(seg => this.map.removeLayer(seg));
          }
          data.trailSegments = [];

          // Recreate colored segments with new color scheme
          for (let i = 0; i < data.positions.length - 1; i++) {
            if (data.positionDataArray && data.positionDataArray[i]) {
              const segmentColor = this.getColorForAircraft(data.positionDataArray[i], data.baseColor, cookie);
              if (!segmentColor) continue;
              const segment = L.polyline([data.positions[i], data.positions[i + 1]], {
                color: segmentColor,
                weight: 3,
                opacity: 0.9,
                smoothFactor: 1,
                lineCap: 'round',
                lineJoin: 'round',
                className: 'aircraft-trail'
              });
              // Only add to map if not hidden
              if (!isHidden) {
                segment.addTo(this.map);
              }
              data.trailSegments.push(segment);
            }
          }
        }
      }
    });
  }

  /**
   * Converts HSL color to hex for brightness adjustments
   */
  hslToHex(h, s, l) {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
    else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
    else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
    else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
    else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
    else if (h >= 300 && h < 360) { r = c; g = 0; b = x; }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  /**
   * Sets the maximum trail points
   */
  setMaxTrailPoints(points) {
    this.maxTrailPoints = points;
    // Update existing trails
    this.aircraft.forEach((data) => {
      // Check if trail exists (might be null in replay mode)
      if (!data.trail) return;

      if (points === 0) {
        data.trail.setLatLngs([]);
      } else if (data.positions.length > points) {
        data.positions = data.positions.slice(-points);
        data.trail.setLatLngs(data.positions);
      }
    });
  }

  /**
   * Sets the info label type
   */
  setInfoLabel(labelType) {
    this.infoLabel = labelType;
    // Update all existing markers
    this.aircraft.forEach((data, cookie) => {
      const position = this.getPositionData(cookie);
      if (position) {
        const label = this.getLabel(position);
        const newIcon = this.createAircraftIcon(data.lastHeading, data.color, label);
        data.marker.setIcon(newIcon);
      }
    });
  }

  /**
   * Sets the units system
   */
  setUnits(units) {
    this.units = units;
    // Update all existing popups and labels
    this.aircraft.forEach((data, cookie) => {
      const position = this.getPositionData(cookie);
      if (position) {
        data.marker.setPopupContent(this.createPopupContent(position));
        const label = this.getLabel(position);
        const newIcon = this.createAircraftIcon(data.lastHeading, data.color, label);
        data.marker.setIcon(newIcon);
      }
    });
  }

  formatFinishAltDelta(valueMeters) {
    if (!Number.isFinite(valueMeters)) {
      return '--';
    }

    const scaledValue = this.units === 'imperial'
      ? Math.round(valueMeters * 3.28084)
      : Math.round(valueMeters);
    const unit = this.units === 'imperial' ? 'ft' : 'm';
    const sign = scaledValue > 0 ? '+' : '';
    return `${sign}${scaledValue}${unit}`;
  }

  /**
   * Gets position data for a cookie (stored for label/popup updates)
   */
  getPositionData(cookie) {
    const data = this.aircraft.get(cookie);
    return data ? data.positionData : null;
  }

  /**
   * Gets the label text based on current settings
   */
  getLabel(position) {
    // Ensure id is always a string
    const id = String(position.id_cn || position.cookie || '');

    switch (this.infoLabel) {
      case 'speed':
        const speedMps = parseFloat(position.speed_mps);
        if (!isNaN(speedMps)) {
          const speed = this.units === 'imperial'
            ? (speedMps * 1.94384).toFixed(0) // m/s to knots
            : (speedMps * 3.6).toFixed(0); // m/s to km/h
          const unit = this.units === 'imperial' ? 'kt' : 'kph';
          return `${id}\n${speed}${unit}`;
        }
        return id;
      case 'altitude':
        const altM = parseFloat(position.alt_m || position.altitude_m);
        if (!isNaN(altM)) {
          const alt = this.units === 'imperial'
            ? Math.round(altM * 3.28084) // m to ft
            : Math.round(altM);
          const unit = this.units === 'imperial' ? 'ft' : 'm';
          return `${id}\n${alt}${unit}`;
        }
        return id;
      case 'vario':
        const varioMps = parseFloat(position.vario_mps);
        if (!isNaN(varioMps)) {
          const vario = this.units === 'imperial'
            ? (varioMps * 1.94384).toFixed(1) // m/s to knots
            : varioMps.toFixed(1); // m/s
          const unit = this.units === 'imperial' ? 'kt' : 'm/s';
          return `${id}\n${vario}${unit}`;
        }
        return id;
      case 'vario_avg':
        // Use 30-second averaged vario
        const aircraftData = this.aircraft.get(position.cookie);
        const varioAvgMps = aircraftData?.varioAvg ?? position.vario_mps;
        if (varioAvgMps != null) {
          const varioAvg = this.units === 'imperial'
            ? (varioAvgMps * 1.94384).toFixed(1) // m/s to knots
            : varioAvgMps.toFixed(1); // m/s
          const unit = this.units === 'imperial' ? 'kt' : 'm/s';
          return `${id}\n${varioAvg}${unit}`;
        }
        return id;
      case 'vario_kalman':
        // Use Kalman filtered vario
        const aircraftDataK = this.aircraft.get(position.cookie);
        const varioKalmanMps = aircraftDataK?.varioKalman?.x ?? position.vario_mps;
        if (varioKalmanMps != null) {
          const varioKalman = this.units === 'imperial'
            ? (varioKalmanMps * 1.94384).toFixed(1) // m/s to knots
            : varioKalmanMps.toFixed(1); // m/s
          const unit = this.units === 'imperial' ? 'kt' : 'm/s';
          return `${id}\n${varioKalman}${unit}`;
        }
        return id;
      case 'vario_te':
        // Use Total Energy compensated vario
        const aircraftDataTE = this.aircraft.get(position.cookie);
        const varioTEMps = aircraftDataTE?.varioTE ?? position.vario_mps;
        if (varioTEMps != null) {
          const varioTE = this.units === 'imperial'
            ? (varioTEMps * 1.94384).toFixed(1) // m/s to knots
            : varioTEMps.toFixed(1); // m/s
          const unit = this.units === 'imperial' ? 'kt' : 'm/s';
          return `${id}\n${varioTE}${unit}`;
        }
        return id;
      case 'etf_delta': {
        const deltaSeconds = position.etfDeltaSeconds;
        const windowSeconds = position.etfTrendSec;
        const denom = Number.isFinite(windowSeconds) ? `${Math.round(windowSeconds)}s` : '';
        const title = denom ? `XC Pace ${denom}` : 'XC Pace';
        const value = Number.isFinite(deltaSeconds) ? `${deltaSeconds > 0 ? '+' : ''}${Math.round(deltaSeconds)}s` : '--';
        const nowMs = Date.now();
        const showTitle = nowMs < (window.etfLabelShowUntilMs || 0);
        return showTitle ? `${id}\n${title}\n${value}` : `${id}\n${value}`;
      }
      case 'etf_delta_b': {
        const deltaSeconds = position.etfDeltaSecondsB;
        const windowSeconds = position.etfTrendSecB || position.etfTrendSec;
        let windowLabel = '';
        if (Number.isFinite(windowSeconds) && windowSeconds > 0) {
          const minutes = windowSeconds / 60;
          const roundedToTenth = Math.round(minutes * 10) / 10;
          const hasDecimal = Math.abs(roundedToTenth - Math.round(roundedToTenth)) > 1e-6;
          const minuteText = hasDecimal ? roundedToTenth.toFixed(1) : roundedToTenth.toFixed(0);
          windowLabel = `${minuteText}min`;
        }
        const title = windowLabel ? `XC Pace ${windowLabel}` : 'XC Pace';
        const value = Number.isFinite(deltaSeconds) ? `${deltaSeconds > 0 ? '+' : ''}${Math.round(deltaSeconds)}s` : '--';
        const nowMs = Date.now();
        const showTitle = nowMs < (window.etfLabelShowUntilMs || 0);
        return showTitle ? `${id}\n${title}\n${value}` : `${id}\n${value}`;
      }
      case 'ddh': {
        return `${id}\n${this.formatFinishAltDelta(position.finishAltDeltaM)}`;
      }
      case 'id':
      default:
        return id;
    }
  }

  /**
   * Creates a modern triangular arrowhead SVG icon with label
   */
  createAircraftIcon(heading = 0, color = '#3b82f6', label = '') {
    const safeColor = (typeof color === 'string' && color.trim().length > 0) ? color : '#3b82f6';
    // Parse HSL color for brightness adjustments
    let lightColor, darkColor;
    if (safeColor.startsWith('hsl')) {
      const match = safeColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
      if (match) {
        const h = parseInt(match[1]);
        const s = parseInt(match[2]);
        const l = parseInt(match[3]);
        lightColor = `hsl(${h}, ${s}%, ${Math.min(l + 15, 90)}%)`;
        darkColor = `hsl(${h}, ${s}%, ${Math.max(l - 15, 20)}%)`;
      }
    } else {
      lightColor = this.adjustColorBrightness(safeColor, 40);
      darkColor = this.adjustColorBrightness(safeColor, -20);
    }

    const uniqueId = Math.random().toString(36).substr(2, 9);

    // Split label by newlines for multi-line support
    const labelLines = (label || '')
      .toString()
      .split('\n')
      .map(line => (line == null ? '' : String(line)));
    const baseY = 40;
    const lineHeight = 12;
    const svgWidth = 60; // Increased from 36 to allow more space for labels
    const svgHeight = 50 + ((labelLines.length - 1) * lineHeight);

    // Generate text elements for each line
    let labelSvg = '';
    labelLines.forEach((line, index) => {
      const safeLine = line || '';
      const isTitleLine = (labelLines.length > 1) && index === 0
        && (safeLine.startsWith('ETA') || safeLine.startsWith('XC Pace'));
      const fontSize = isTitleLine ? 10 : 12;
      const y = baseY + (index * lineHeight);
      // White stroke outline
      labelSvg += `
        <text x="${svgWidth / 2}" y="${y}" 
              text-anchor="middle" 
              font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" 
              font-size="${fontSize}" 
              font-weight="600"
              stroke="white" 
              stroke-width="2"
              stroke-linejoin="round"
              paint-order="stroke fill">${safeLine}</text>`;
      // Black text
      labelSvg += `
        <text x="${svgWidth / 2}" y="${y}" 
              text-anchor="middle" 
              font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" 
              font-size="${fontSize}" 
              font-weight="600"
              fill="#000000">${safeLine}</text>`;
    });

    const svg = `
      <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="arrowGradient-${uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:${lightColor};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
          </linearGradient>
          <filter id="glow-${uniqueId}">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <g transform="translate(${svgWidth / 2}, 18) rotate(${heading}) translate(-${svgWidth / 2}, -18)">
          <!-- Directional arrowhead with notched back -->
          <path d="M ${svgWidth / 2} 8 L ${svgWidth / 2 + 8} 24 L ${svgWidth / 2} 20 L ${svgWidth / 2 - 8} 24 Z" 
                fill="url(#arrowGradient-${uniqueId})" 
                stroke="${darkColor}" 
                stroke-width="1.5"
                stroke-linejoin="round"
                filter="url(#glow-${uniqueId})"/>
        </g>
        <!-- Label below icon (not rotated, always horizontal) -->
        ${labelSvg}
      </svg>
    `;

    return L.divIcon({
      className: 'aircraft-marker',
      html: `<div class="aircraft-arrow">${svg}</div>`,
      iconSize: [svgWidth, svgHeight],
      iconAnchor: [svgWidth / 2, 18], // Keep anchor at triangle center, not affected by label
      popupAnchor: [0, -18]
    });
  }

  /**
   * Adjusts color brightness
   */
  adjustColorBrightness(color, percent) {
    // Convert hex to RGB
    let r, g, b;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else if (color.startsWith('hsl')) {
      // For HSL colors, just return as is
      return color;
    } else {
      return color;
    }

    // Adjust brightness
    r = Math.max(0, Math.min(255, r + percent));
    g = Math.max(0, Math.min(255, g + percent));
    b = Math.max(0, Math.min(255, b + percent));

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Creates popup content for an aircraft
   */
  createPopupContent(position) {
    const {
      id_cn = 'Unknown',
      id_aircraft = 'N/A',
      id_fname = '',
      id_lname = '',
      alt_m,
      altitude_m,
      speed_mps,
      vario_mps,
      heading_deg,
      aircraft_type,
      aircraft,
      id_model
    } = position;

    const fullName =
      [id_fname, id_lname].filter(n => n).join(' ').trim() ||
      position.pilotName ||
      position.pilot_name ||
      position.metadata?.pilotName ||
      id_cn ||
      position.callsign ||
      'Unknown Pilot';

    // Get glider type (API returns aircraft_type, live sends aircraft, or id_model)
    const gliderType = aircraft_type || aircraft || id_aircraft || id_model || 'N/A';

    // Parse values from API (they come as strings!)
    const altMeters = parseFloat(alt_m || altitude_m);
    const speedMps = parseFloat(speed_mps);
    const varioMps = parseFloat(vario_mps);
    const headingDeg = parseFloat(heading_deg);

    // Convert units based on settings
    let altValue, altUnit, speedValue, speedUnit, varioValue, varioUnit;

    if (this.units === 'imperial') {
      altValue = !isNaN(altMeters) ? Math.round(altMeters * 3.28084) : null;
      altUnit = 'ft';
      speedValue = !isNaN(speedMps) ? (speedMps * 1.94384).toFixed(1) : null;
      speedUnit = 'kts';
      varioValue = !isNaN(varioMps) ? (varioMps * 1.94384).toFixed(1) : null;
      varioUnit = 'kts';
    } else {
      altValue = !isNaN(altMeters) ? Math.round(altMeters) : null;
      altUnit = 'm';
      speedValue = !isNaN(speedMps) ? (speedMps * 3.6).toFixed(1) : null;
      speedUnit = 'km/h';
      varioValue = !isNaN(varioMps) ? varioMps.toFixed(1) : null;
      varioUnit = 'm/s';
    }

    const cookie = position.cookie;
    const isFollowing = this.followingCookie === cookie;

    return `
      <div class="aircraft-popup">
        <div class="popup-header">${fullName}</div>
        <div class="popup-row">
          <span class="popup-label">ID</span>
          <span class="popup-value">${id_cn}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Glider</span>
          <span class="popup-value">${gliderType}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Alt</span>
          <span class="popup-value">${altValue != null ? `${altValue} ${altUnit}` : 'N/A'}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Speed</span>
          <span class="popup-value">${speedValue != null ? `${speedValue} ${speedUnit}` : 'N/A'}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Vario</span>
          <span class="popup-value">${varioValue != null ? `${varioValue} ${varioUnit}` : 'N/A'}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Heading</span>
          <span class="popup-value">${!isNaN(headingDeg) ? `${Math.round(headingDeg)}°` : 'N/A'}</span>
        </div>
        <button class="popup-follow-btn ${isFollowing ? 'following' : ''}" data-cookie="${cookie}">
          ${isFollowing ? 'Unfollow' : 'Follow'}
        </button>
      </div>
    `;
  }

  /**
   * Updates or creates an aircraft marker and trail
   * @param {object} position - Position data
   */
  updateAircraft(position) {
    const { cookie, lat, lon, heading_deg } = position;

    if (!lat || !lon || !cookie) return;

    let aircraftData = this.aircraft.get(cookie);

    if (!aircraftData) {
      // Create new aircraft with unique base color
      const baseColor = this.generateUniqueColor();
      const currentColor = this.getColorForAircraft(position, baseColor);
      const markerColor = currentColor || baseColor;
      const label = this.getLabel(position);
      const iconHeading = this.getIconHeadingDegrees(heading_deg || 0);
      const icon = this.createAircraftIcon(iconHeading, markerColor, label);
      const marker = L.marker([lat, lon], { icon })
        .bindPopup(this.createPopupContent(position));

      // Only add to map if not hidden
      if (!this.hiddenCookies.has(cookie)) {
        marker.addTo(this.map);
      }

      // For non-aircraft schemes, we'll use individual polylines for each segment
      // For aircraft scheme, use single polyline
      const trailSegments = [];

      // Initialize 30-second averaged vario with current vario
      const initialVario = position.vario_mps ?? 0;
      const initialSpeed = position.speed_mps ?? 0;

      // Create initial position with averaged vario values
      const initialPosition = {
        ...position,
        vario_avg_mps: initialVario,
        vario_kalman_mps: initialVario,
        vario_te_mps: initialVario,
        etfDeltaSeconds: position.etfDeltaSeconds ?? null,
        etfTrendSec: position.etfTrendSec ?? null
      };

      aircraftData = {
        marker,
        trail: null, // Will be created for aircraft scheme
        trailSegments: trailSegments, // Array of polylines for colored schemes
        positions: [[lat, lon]],
        positionDataArray: [initialPosition], // Store position data for each point
        lastHeading: heading_deg || 0,
        baseColor: baseColor, // Store base color for reverting to aircraft scheme
        lastColor: markerColor, // Cache current color for interpolated frames
        lastLabel: label, // Cache current label for interpolated frames
        lastIconKey: this.getAircraftIconKey(iconHeading, markerColor, label),
        lastIconHeading: iconHeading,
        positionData: position, // Current position data
        varioAvg: initialVario, // 30-second exponential moving average of vario (m/s)
        varioKalman: { x: initialVario, p: 1.0 }, // Kalman filter state {x: estimate, p: error covariance}
        varioTE: initialVario, // Total Energy compensated vario (m/s)
        lastVarioUpdate: Date.now(), // Timestamp of last vario update
        prevSpeed: initialSpeed, // Previous speed for TE calculation
        lastSpeedUpdate: Date.now() // Timestamp of last speed update
      };

      this.aircraft.set(cookie, aircraftData);
    } else {
      // Update existing aircraft
      const { marker, positions, lastHeading } = aircraftData;

      // Store position data for later use
      aircraftData.positionData = position;

      // Update 30-second averaged vario using Exponential Moving Average
      if (position.vario_mps != null) {
        const now = Date.now();
        const timeDelta = (now - aircraftData.lastVarioUpdate) / 1000; // seconds

        // Only update if we have a valid time delta (not too large)
        if (timeDelta > 0 && timeDelta < 60) {
          // EMA: new_avg = α × current + (1 - α) × old_avg
          aircraftData.varioAvg = this.varioEmaAlpha * position.vario_mps +
            (1 - this.varioEmaAlpha) * aircraftData.varioAvg;

          // Kalman filter update
          aircraftData.varioKalman = this.kalmanUpdate(
            aircraftData.varioKalman,
            position.vario_mps,
            timeDelta
          );

          // Total Energy vario calculation
          if (position.speed_mps != null && aircraftData.prevSpeed != null) {
            const speedTimeDelta = (now - aircraftData.lastSpeedUpdate) / 1000;
            aircraftData.varioTE = this.calculateTEVario(
              position.vario_mps,
              position.speed_mps,
              aircraftData.prevSpeed,
              speedTimeDelta
            );
            aircraftData.prevSpeed = position.speed_mps;
            aircraftData.lastSpeedUpdate = now;
          } else {
            // No speed data, use raw vario
            aircraftData.varioTE = position.vario_mps;
            if (position.speed_mps != null) {
              aircraftData.prevSpeed = position.speed_mps;
              aircraftData.lastSpeedUpdate = now;
            }
          }

          aircraftData.lastVarioUpdate = now;
        } else if (timeDelta >= 60) {
          // If gap is too large, reset to current value
          aircraftData.varioAvg = position.vario_mps;
          aircraftData.varioKalman = { x: position.vario_mps, p: 1.0 };
          aircraftData.varioTE = position.vario_mps;
          aircraftData.lastVarioUpdate = now;
          if (position.speed_mps != null) {
            aircraftData.prevSpeed = position.speed_mps;
            aircraftData.lastSpeedUpdate = now;
          }
        }
      }

      // Smooth position update
      marker.setLatLng([lat, lon]);

      // Get current color based on scheme (pass cookie for vario_avg)
      const currentColor = this.getColorForAircraft(position, aircraftData.baseColor, cookie);
      const markerColor = currentColor || aircraftData.baseColor;

      // Cache color for trail segments (always updated regardless of label throttle)
      aircraftData.lastColor = markerColor;

      // Simple per-aircraft timer: only recompute label when enough time has passed.
      // Between refreshes, the previous label persists (no flicker, no "--").
      const _now = performance.now();
      const _elapsed = _now - (aircraftData._lastLabelTime || 0);
      const _shouldUpdateLabel = this.labelRefreshMs <= 0 || _elapsed >= this.labelRefreshMs;

      if (_shouldUpdateLabel) {
        aircraftData._lastLabelTime = _now;
        const currentLabel = this.getLabel(position);
        aircraftData.lastLabel = currentLabel;
      }

      // Only update the marker icon when the visible heading/color/label changed.
      this.applyAircraftIcon(
        aircraftData,
        heading_deg != null ? heading_deg : lastHeading,
        markerColor,
        aircraftData.lastLabel
      );

      if (heading_deg != null) {
        aircraftData.lastHeading = heading_deg;
      }

      // Update popup content (follows same throttle as labels)
      if (_shouldUpdateLabel) {
        marker.setPopupContent(this.createPopupContent(position));
      }

      // Center map on this aircraft if following
      if (this.followingCookie === cookie) {
        this.centerOnFollowed();
      }

      // Update trail only if trail length > 0
      if (this.maxTrailPoints > 0) {
        // Check distance from last position to detect jumps/seeks
        const MAX_SEGMENT_DISTANCE_KM = 5; // Max 5km between trail points
        let shouldAddPoint = true;
        let trimmedOldestPoint = false;

        if (positions.length > 0) {
          const lastPos = positions[positions.length - 1];
          // Quick equirectangular distance approximation (avoid trig for every frame)
          const dLat = (lat - lastPos[0]) * 111.32; // km per degree latitude
          const dLon = (lon - lastPos[1]) * 111.32 * Math.cos(lastPos[0] * 0.01745329);
          const distanceSq = dLat * dLat + dLon * dLon;

          // If jump is too large, clear trail instead of drawing long line
          if (distanceSq > MAX_SEGMENT_DISTANCE_KM * MAX_SEGMENT_DISTANCE_KM) {
            positions.length = 0; // Clear trail
            if (aircraftData.positionDataArray) {
              aircraftData.positionDataArray.length = 0;
            }
            if (aircraftData.trailSegments) {
              aircraftData.trailSegments.forEach(seg => this.map.removeLayer(seg));
              aircraftData.trailSegments = [];
            }
            this.clearPendingTrailHistory(aircraftData);
            shouldAddPoint = true; // Start new trail from this position
          }
        }

        if (shouldAddPoint) {
          positions.push([lat, lon]);
        }

        // Keep only last N points for performance
        if (positions.length > this.maxTrailPoints) {
          positions.shift();
          trimmedOldestPoint = true;
          if (aircraftData.positionDataArray && aircraftData.positionDataArray.length > 0) {
            aircraftData.positionDataArray.shift();
          }
        }

        // Store only trail-coloring fields (avoid spreading full position object)
        if (!aircraftData.positionDataArray) {
          aircraftData.positionDataArray = [];
        }
        aircraftData.positionDataArray.push({
          alt_m: position.alt_m,
          altitude_m: position.altitude_m,
          altitude_ft: position.altitude_ft,
          speed_mps: position.speed_mps,
          speed_kt: position.speed_kt,
          vario_mps: position.vario_mps,
          vario_avg_mps: aircraftData.varioAvg,
          vario_kalman_mps: aircraftData.varioKalman.x,
          vario_te_mps: aircraftData.varioTE,
          etfDeltaSeconds: position.etfDeltaSeconds ?? aircraftData.positionData?.etfDeltaSeconds ?? null,
          etfTrendSec: position.etfTrendSec ?? aircraftData.positionData?.etfTrendSec ?? null
        });
        while (aircraftData.positionDataArray.length > positions.length) {
          aircraftData.positionDataArray.shift();
        }

        // Update trail based on color scheme
        const isHidden = this.hiddenCookies.has(cookie);

        if (this.colorScheme === 'aircraft') {
          // Use single polyline with base color
          if (!aircraftData.trail) {
            aircraftData.trail = L.polyline(positions, {
              color: aircraftData.baseColor,
              weight: 3,
              opacity: 0.7,
              smoothFactor: 1,
              className: 'aircraft-trail'
            });
            // Only add to map if not hidden
            if (!isHidden) {
              aircraftData.trail.addTo(this.map);
            }
          } else {
            aircraftData.trail.setLatLngs(positions);
          }

          // Clear any existing trail segments
          if (aircraftData.trailSegments) {
            aircraftData.trailSegments.forEach(seg => this.map.removeLayer(seg));
            aircraftData.trailSegments = [];
          }
        } else {
          // Use colored segments for altitude/speed/vario schemes
          // Remove old single trail if it exists
          if (aircraftData.trail) {
            this.map.removeLayer(aircraftData.trail);
            aircraftData.trail = null;
          }

          if (!aircraftData.trailSegments) {
            aircraftData.trailSegments = [];
          }

          const syncSegment = (segment, index) => {
            const segmentColor = this.getColorForAircraft(aircraftData.positionDataArray[index], aircraftData.baseColor, cookie);
            segment.setLatLngs([positions[index], positions[index + 1]]);
            segment.setStyle({ color: segmentColor });

            if (isHidden) {
              if (this.map.hasLayer(segment)) {
                this.map.removeLayer(segment);
              }
            } else if (!this.map.hasLayer(segment)) {
              segment.addTo(this.map);
            }
          };

          // Incremental update: only add the newest segment and trim oldest
          const neededSegments = positions.length - 1;

          if (neededSegments > 0) {
            // Trim excess segments from the front (when positions were shifted)
            while (aircraftData.trailSegments.length > neededSegments) {
              const removed = aircraftData.trailSegments.shift();
              this.map.removeLayer(removed);
            }

            // Once the trail is full, reuse the oldest segment at the head so the
            // visible trail keeps advancing instead of freezing at max length.
            if (trimmedOldestPoint && aircraftData.trailSegments.length === neededSegments) {
              const recycled = aircraftData.trailSegments.shift();
              syncSegment(recycled, neededSegments - 1);
              aircraftData.trailSegments.push(recycled);
            }

            // Add any missing segments. This covers trail startup and recovery from clears.
            while (aircraftData.trailSegments.length < neededSegments) {
              const i = aircraftData.trailSegments.length;
              const segment = L.polyline([positions[i], positions[i + 1]], {
                color: aircraftData.baseColor,
                weight: 3,
                opacity: 0.9,
                smoothFactor: 1,
                lineCap: 'round',
                lineJoin: 'round',
                className: 'aircraft-trail'
              });
              syncSegment(segment, i);
              aircraftData.trailSegments.push(segment);
            }

            if (!trimmedOldestPoint && aircraftData.trailSegments.length > 0) {
              // Under the max trail length, only the newest segment changes.
              syncSegment(aircraftData.trailSegments[aircraftData.trailSegments.length - 1], neededSegments - 1);
            }
          }
        }
      } else {
        // Clear trail if disabled
        if (aircraftData.trail) {
          this.map.removeLayer(aircraftData.trail);
          aircraftData.trail = null;
        }
        if (aircraftData.trailSegments) {
          aircraftData.trailSegments.forEach(seg => this.map.removeLayer(seg));
          aircraftData.trailSegments = [];
        }
      }
    }
  }

  /**
   * Refreshes only the label and icon for an aircraft (no trail/vario updates).
   * Used for steady-rate label refreshes that are decoupled from sample boundaries.
   * @param {object} position - Position data with cookie, heading_deg, etc.
   */
  refreshLabel(position) {
    const aircraftData = this.aircraft.get(position.cookie);
    if (!aircraftData) return;

    const label = this.getLabel(position);
    const color = aircraftData.lastColor || aircraftData.baseColor;
    const heading = position.heading_deg ?? aircraftData.lastHeading;

    aircraftData.lastLabel = label;
    this.applyAircraftIcon(aircraftData, heading, color, label);
    aircraftData.marker.setPopupContent(this.createPopupContent(position));
  }

  /**
   * Removes an aircraft from the map
   */
  removeAircraft(cookie) {
    const aircraftData = this.aircraft.get(cookie);
    if (aircraftData) {
      this.map.removeLayer(aircraftData.marker);
      if (aircraftData.trail) {
        this.map.removeLayer(aircraftData.trail);
      }
      if (aircraftData.trailSegments) {
        aircraftData.trailSegments.forEach(seg => this.map.removeLayer(seg));
      }
      if (aircraftData.pendingSegment) {
        this.map.removeLayer(aircraftData.pendingSegment);
      }
      this.clearPendingTrailHistory(aircraftData);
      this.usedColors.delete(aircraftData.baseColor);
      this.aircraft.delete(cookie);
    }
  }

  /**
   * Updates only the marker position and heading for smooth interpolated movement.
   * Draws a temporary "pending" trail segment from the last 1Hz point to current position.
   * The pending segment is replaced when the next 1Hz update arrives.
   * This is called every animation frame for smooth movement in replay mode.
   * @param {object} position - Position data with lat, lon, heading_deg, cookie
   */
  updateAircraftMarkerOnly(position) {
    const { cookie, lat, lon, heading_deg } = position;

    if (!lat || !lon || !cookie) return;

    const aircraftData = this.aircraft.get(cookie);
    if (!aircraftData) {
      // Aircraft doesn't exist yet - let updateAircraft create it
      return;
    }

    const { marker, positions } = aircraftData;

    // Update marker position (smooth movement)
    marker.setLatLng([lat, lon]);

    if (heading_deg != null) {
      // Use cached color and label from last updateAircraft() call so interpolated frames
      // do not force label/color churn when only heading changes slightly.
      const markerColor = aircraftData.lastColor || aircraftData.baseColor;
      const currentLabel = aircraftData.lastLabel || '';
      this.applyAircraftIcon(aircraftData, heading_deg, markerColor, currentLabel);
      aircraftData.lastHeading = heading_deg;
    }

    // Draw a short sampled trail head so replay lays the trail down smoothly.
    if (this.maxTrailPoints > 0 && !this.hiddenCookies.has(cookie) && positions.length > 0) {
      const pendingDisplayPosition = {
        ...(aircraftData.positionData || {}),
        ...position,
        vario_avg_mps: aircraftData.varioAvg,
        vario_kalman_mps: aircraftData.varioKalman?.x,
        vario_te_mps: aircraftData.varioTE
      };
      const segmentColor =
        this.getColorForAircraft(pendingDisplayPosition, aircraftData.baseColor, cookie) ||
        aircraftData.lastColor ||
        aircraftData.baseColor;
      const pendingTrailHistory = this.updatePendingTrailHistory(aircraftData, pendingDisplayPosition);
      const segmentLatLngs = pendingTrailHistory.length >= 2
        ? pendingTrailHistory.map(point => [point.lat, point.lon])
        : [positions[positions.length - 1], [lat, lon]];

      if (segmentLatLngs.length >= 2) {
        if (aircraftData.pendingSegment) {
          aircraftData.pendingSegment.setLatLngs(segmentLatLngs);
          if (aircraftData.pendingSegmentColor !== segmentColor) {
            aircraftData.pendingSegment.setStyle({ color: segmentColor });
            aircraftData.pendingSegmentColor = segmentColor;
          }
        } else {
          aircraftData.pendingSegment = L.polyline(segmentLatLngs, {
            color: segmentColor,
            weight: 3,
            opacity: 0.9,
            smoothFactor: 1,
            lineCap: 'round',
            lineJoin: 'round',
            className: 'aircraft-trail aircraft-trail-pending'
          }).addTo(this.map);
          aircraftData.pendingSegmentColor = segmentColor;
        }
      } else if (aircraftData.pendingSegment) {
        this.map.removeLayer(aircraftData.pendingSegment);
        aircraftData.pendingSegment = null;
        aircraftData.pendingSegmentColor = null;
      }
    } else if (aircraftData.pendingSegment) {
      this.map.removeLayer(aircraftData.pendingSegment);
      aircraftData.pendingSegment = null;
      aircraftData.pendingSegmentColor = null;
      this.clearPendingTrailHistory(aircraftData);
    }

    // Center map on this aircraft if following
    if (this.followingCookie === cookie) {
      this.centerOnFollowed();
    }
  }

  /**
   * Gets the count of active aircraft
   */
  getAircraftCount() {
    return this.aircraft.size;
  }

  /**
   * Clears all aircraft from the map
   */
  clearAll() {
    this.aircraft.forEach((data) => {
      this.map.removeLayer(data.marker);
      if (data.trail) {
        this.map.removeLayer(data.trail);
      }
      if (data.trailSegments) {
        data.trailSegments.forEach(seg => this.map.removeLayer(seg));
      }
      if (data.pendingSegment) {
        this.map.removeLayer(data.pendingSegment);
      }
      this.clearPendingTrailHistory(data);
    });
    this.aircraft.clear();
  }

  /**
   * Clears all trails but keeps aircraft markers (used when seeking in replay)
   */
  clearAllTrails() {
    this.aircraft.forEach((data) => {
      // Clear trail polyline from map
      if (data.trail) {
        this.map.removeLayer(data.trail);
        data.trail = null;
      }
      // Clear trail segments from map
      if (data.trailSegments) {
        data.trailSegments.forEach(seg => this.map.removeLayer(seg));
        data.trailSegments = [];
      }
      // Clear pending interpolation segment
      if (data.pendingSegment) {
        this.map.removeLayer(data.pendingSegment);
        data.pendingSegment = null;
        data.pendingSegmentColor = null;
      }
      this.clearPendingTrailHistory(data);
      // Clear position arrays (trail data)
      if (data.positions) {
        data.positions.length = 0;
      }
      if (data.positionDataArray) {
        data.positionDataArray.length = 0;
      }
    });
  }

  /**
   * Gets all aircraft positions for bounds calculation
   */
  getAllPositions() {
    const positions = [];
    this.aircraft.forEach((data) => {
      const latLng = data.marker.getLatLng();
      positions.push([latLng.lat, latLng.lng]);
    });
    return positions;
  }

  /**
   * Fits map to show all aircraft
   * @param {number} maxZoom - Optional maximum zoom level to apply
   */
  fitBounds(maxZoom = null) {
    const positions = this.getAllPositions();
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      const options = { padding: [50, 50] };
      if (maxZoom !== null) {
        options.maxZoom = maxZoom;
      }
      this.map.fitBounds(bounds, options);
    }
  }

  /**
   * Fits map to show specific coordinates (for replay mode)
   * @param {Array} coords - Array of [lat, lon] coordinates
   * @param {number} maxZoom - Optional maximum zoom level to apply
   */
  fitBoundsToCoords(coords, maxZoom = null) {
    if (!coords || coords.length === 0) {
      console.warn('⚠️ fitBoundsToCoords: No coordinates provided');
      return;
    }

    // Filter out invalid coordinates
    const validCoords = coords.filter(coord =>
      Array.isArray(coord) &&
      coord.length >= 2 &&
      coord[0] !== undefined && coord[0] !== null && !isNaN(coord[0]) &&
      coord[1] !== undefined && coord[1] !== null && !isNaN(coord[1])
    );

    if (validCoords.length === 0) {
      console.warn('⚠️ fitBoundsToCoords: No valid coordinates after filtering');
      return;
    }

    try {
      const bounds = L.latLngBounds(validCoords);
      const options = { padding: [50, 50] };
      if (maxZoom !== null) {
        options.maxZoom = maxZoom;
      }
      this.map.fitBounds(bounds, options);
    } catch (error) {
      console.error('❌ fitBoundsToCoords: Error fitting bounds:', error);
    }
  }

  /**
   * Calculate distance between two coordinates in kilometers
   * Uses Haversine formula
   * @param {number} lat1 - Latitude of first point
   * @param {number} lon1 - Longitude of first point
   * @param {number} lat2 - Latitude of second point
   * @param {number} lon2 - Longitude of second point
   * @returns {number} Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Sets the follow callback for UI updates
   */
  setFollowCallback(callback) {
    this.followCallback = callback;
  }

  /**
   * Starts following an aircraft
   */
  followAircraft(cookie) {
    this.followingCookie = cookie;
    const aircraftData = this.aircraft.get(cookie);
    if (aircraftData && this.followCallback) {
      const callsign = aircraftData.positionData?.id_cn || 'Unknown';
      this.followCallback(callsign);
    }
    // Update all popups to reflect follow state
    this.updateAllPopups();
  }

  /**
   * Stops following the current aircraft
   */
  unfollowAircraft() {
    this.followingCookie = null;
    if (this.followCallback) {
      this.followCallback(null);
    }
    // Update all popups to reflect follow state
    this.updateAllPopups();
  }

  /**
   * Updates all aircraft popups (used when follow state changes)
   */
  updateAllPopups() {
    this.aircraft.forEach((data) => {
      if (data.positionData) {
        data.marker.setPopupContent(this.createPopupContent(data.positionData));
      }
    });
  }

  /**
   * Centers map on followed aircraft if following
   */
  centerOnFollowed() {
    if (this.followingCookie) {
      const aircraftData = this.aircraft.get(this.followingCookie);
      if (aircraftData) {
        const latLng = aircraftData.marker.getLatLng();
        this.map.panTo(latLng, { animate: true, duration: 0.5 });
      }
    }
  }

  /**
   * Filters aircraft to show only specified cookies
   */
  filterAircraft(visibleCookies) {
    this.aircraft.forEach((data, cookie) => {
      const shouldShow = visibleCookies.has(cookie);

      // Show/hide marker
      if (shouldShow) {
        if (!this.map.hasLayer(data.marker)) {
          data.marker.addTo(this.map);
        }
      } else {
        if (this.map.hasLayer(data.marker)) {
          this.map.removeLayer(data.marker);
        }
      }

      // Show/hide trail
      if (data.trail) {
        if (shouldShow) {
          if (!this.map.hasLayer(data.trail)) {
            data.trail.addTo(this.map);
          }
        } else {
          if (this.map.hasLayer(data.trail)) {
            this.map.removeLayer(data.trail);
          }
        }
      }

      // Show/hide trail segments
      if (data.trailSegments && data.trailSegments.length > 0) {
        data.trailSegments.forEach(segment => {
          if (shouldShow) {
            if (!this.map.hasLayer(segment)) {
              segment.addTo(this.map);
            }
          } else {
            if (this.map.hasLayer(segment)) {
              this.map.removeLayer(segment);
            }
          }
        });
      }
    });
  }

  /**
   * Sets hidden cookies and applies visibility filtering
   * This method stores the hidden set and applies it immediately
   * @param {Set} hiddenSet - Set of cookies that should be hidden
   */
  setHiddenCookies(hiddenSet) {
    this.hiddenCookies = new Set(hiddenSet);

    // Apply visibility to all aircraft
    this.aircraft.forEach((data, cookie) => {
      const shouldShow = !this.hiddenCookies.has(cookie);

      // Show/hide marker
      if (shouldShow) {
        if (!this.map.hasLayer(data.marker)) {
          data.marker.addTo(this.map);
        }
      } else {
        if (this.map.hasLayer(data.marker)) {
          this.map.removeLayer(data.marker);
        }
      }

      // Show/hide trail
      if (data.trail) {
        if (shouldShow) {
          if (!this.map.hasLayer(data.trail)) {
            data.trail.addTo(this.map);
          }
        } else {
          if (this.map.hasLayer(data.trail)) {
            this.map.removeLayer(data.trail);
          }
        }
      }

      // Show/hide trail segments
      if (data.trailSegments && data.trailSegments.length > 0) {
        data.trailSegments.forEach(segment => {
          if (shouldShow) {
            if (!this.map.hasLayer(segment)) {
              segment.addTo(this.map);
            }
          } else {
            if (this.map.hasLayer(segment)) {
              this.map.removeLayer(segment);
            }
          }
        });
      }
    });
  }

  /**
   * Checks if a cookie is hidden
   * @param {string|number} cookie - The aircraft cookie
   * @returns {boolean} True if hidden
   */
  isHidden(cookie) {
    return this.hiddenCookies.has(cookie);
  }

  /**
   * Shows all aircraft (removes filtering and clears hidden set)
   */
  showAllAircraft() {
    // Clear hidden cookies
    this.hiddenCookies.clear();

    this.aircraft.forEach((data) => {
      // Show marker
      if (!this.map.hasLayer(data.marker)) {
        data.marker.addTo(this.map);
      }

      // Show trail
      if (data.trail && !this.map.hasLayer(data.trail)) {
        data.trail.addTo(this.map);
      }

      // Show trail segments
      if (data.trailSegments && data.trailSegments.length > 0) {
        data.trailSegments.forEach(segment => {
          if (!this.map.hasLayer(segment)) {
            segment.addTo(this.map);
          }
        });
      }
    });
  }
}
